import React, { useState, useEffect } from 'react'
import { useAtom } from 'jotai'
import Sidebar from '../components/layout/Sidebar'
import QuickChartCreator from '../components/charts/QuickChartCreator'
import { UnifiedAIChartCreator } from '../components/charts/UnifiedAIChartCreator'
import SlidingToggle from '../components/ui/SlidingToggle'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
// Recharts imports removed - now using UniversalChartRenderer
import { 
  Plus,
  CloudUpload,
  Clock,
  SortAsc,
  Calendar,
  BarChart3,
  Settings,
  Trash2,
  PencilLine,
  Sparkles,
  FileSpreadsheet,
  Brain
} from 'lucide-react'
import { 
  chartsAtom, 
  removeChartAtom, 
  updateChartAtom,
  type ChartData,
  type ChartStudy,
  type AxisOptions,
  type LocalChartConfiguration,
} from '../store/chartAtoms'
import { useAuth } from '../contexts/AuthContext'
import { currentOrganizationAtom } from '../store/organizationAtoms'
import { ChartService } from '../services/chartService'
import ChartStudiesDialog from '../components/charts/ChartStudiesDialog'
import { 
  CHART_COLORS as COLORS,
  getChartIconComponent
} from '../lib/chartUtils'
import UniversalChartRenderer from '../components/charts/UniversalChartRenderer'
import { normalizeChartData } from '../lib/chartDataNormalizer'
import EditableChartTitle from '../components/charts/EditableChartTitle'
import ChartTypeSelector from '../components/charts/ChartTypeSelector'
import ChartDataEditorDialog from '../components/charts/ChartDataEditorDialog'
import ChartColorPaletteChanger from '../components/ui/ChartColorPaletteChanger'
import { useCustomPalettes, useActiveDefaultPaletteId } from '../contexts/ChartPreferencesContext'
import { useTheme } from '../lib/themeUtils'
import { dataSourcesService } from '../services/dataSourcesService'
import type { Chart as DBChart, ChartType, ChartDataPoint, UpdateChart, ChartColor, ChartDisplayOptions, ChartConfiguration, CreateChart, UniversalChart } from '@shared/schemas/chart' // For database charts
import { convertDatabaseChartToUniversal, convertUniversalToDatabase } from '@shared/schemas/chart'

type SortOption = 'newest' | 'oldest' | 'alphabetical'

const SORT_OPTIONS = [
  {
    value: 'newest',
    label: 'Newest',
    icon: <Clock className="h-3 w-3" />
  },
  {
    value: 'oldest',
    label: 'Oldest',
    icon: <Calendar className="h-3 w-3" />
  },
  {
    value: 'alphabetical',
    label: 'A-Z',
    icon: <SortAsc className="h-3 w-3" />
  }
]

const Charts: React.FC = () => {
  const { currentUser } = useAuth()
  const [currentOrganization] = useAtom(currentOrganizationAtom)
  const [localChartsData] = useAtom(chartsAtom)
  const [, removeLocalChart] = useAtom(removeChartAtom)
  const [, updateLocalChart] = useAtom(updateChartAtom)
  const [showQuickCreate, setShowQuickCreate] = useState(false)
  const [isUnifiedCreatorOpen, setIsUnifiedCreatorOpen] = useState(false)
  const [dataSources, setDataSources] = useState<any[]>([])
  const [loadingDataSources, setLoadingDataSources] = useState(false)
  const customPalettes = useCustomPalettes()
  const isDarkMode = useTheme()
  const activeDefaultPaletteId = useActiveDefaultPaletteId()

  // Sorting state
  const [localChartSort, setLocalChartSort] = useState<SortOption>('newest')
  const [databaseChartSort, setDatabaseChartSort] = useState<SortOption>('newest')

  const [savingCharts, setSavingCharts] = useState<Set<string>>(new Set())
  const [databaseCharts, setDatabaseCharts] = useState<ChartData[]>([])
  const [loadingDatabaseCharts, setLoadingDatabaseCharts] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean
    chartId: string
    chartTitle: string
    isDatabase: boolean
  }>({
    isOpen: false,
    chartId: '',
    chartTitle: '',
    isDatabase: false
  })
  const [studiesDialog, setStudiesDialog] = useState<{
    isOpen: boolean
    chart: ChartData | DBChart | null
    isDatabase: boolean
  }>({
    isOpen: false,
    chart: null,
    isDatabase: false,
  })

  // State for ChartDataEditorDialog
  const [dataEditorDialog, setDataEditorDialog] = useState<{
    isOpen: boolean;
    chart: DBChart | null; // Assuming only DB charts can have their data edited directly for now
    isDatabase: boolean; // To confirm it is a DB chart
  }>({
    isOpen: false,
    chart: null,
    isDatabase: false,
  });

  // State for local chart edit dialog
  const [localEditDialog, setLocalEditDialog] = useState<{
    isOpen: boolean;
    chartTitle: string;
  }>({
    isOpen: false,
    chartTitle: '',
  });

  const loadDbCharts = async () => {
    console.log('[loadDbCharts] Called with user:', currentUser?.uid, 'org:', currentOrganization?.id);
    
    if (!currentUser?.uid || !currentOrganization?.id) {
      console.log('[loadDbCharts] Missing user or organization, clearing charts');
      setDatabaseCharts([]);
      return;
    }
    
    console.log('[loadDbCharts] Starting load, setting loading state...');
    setLoadingDatabaseCharts(true);
    
    try {
      console.log('[loadDbCharts] Fetching charts from ChartService...');
      const chartsFromDb = await ChartService.getUserCharts(currentOrganization.id, currentUser.uid);
      console.log('[loadDbCharts] Received', chartsFromDb.length, 'raw charts from database');
      
      // Convert database charts to UniversalChart format
      const convertedCharts = chartsFromDb.map(convertDatabaseChartToUniversal);
      console.log('[loadDbCharts] Converted', convertedCharts.length, 'charts to universal format');
      
      setDatabaseCharts(convertedCharts);
      console.log('[loadDbCharts] Charts set in state, should trigger re-render');
    } catch (error) {
      console.error('[loadDbCharts] Error loading database charts:', error);
      setDatabaseCharts([]);
    } finally {
      console.log('[loadDbCharts] Setting loading to false');
      setLoadingDatabaseCharts(false);
    }
  };

  // Sorting functions
  const sortCharts = (chartsToSort: Array<ChartData | DBChart>, sortOption: SortOption) => {
    const sorted = [...chartsToSort]
    
    switch (sortOption) {
      case 'newest':
        return sorted.sort((a, b) => (new Date(b.createdAt).getTime()) - (new Date(a.createdAt).getTime()))
      
      case 'oldest':
        return sorted.sort((a, b) => (new Date(a.createdAt).getTime()) - (new Date(b.createdAt).getTime()))
      
      case 'alphabetical':
        return sorted.sort((a, b) => (a.title || 'Z').localeCompare(b.title || 'Z'))
      
      default:
        return sorted
    }
  }

  // Get sorted charts
  const sortedLocalCharts: ChartData[] = sortCharts(localChartsData, localChartSort) as ChartData[];
  const sortedDatabaseCharts: ChartData[] = sortCharts(databaseCharts, databaseChartSort) as ChartData[];

  // Load data sources using the same approach as DataSources page
  const loadDataSources = async () => {
    if (!currentUser?.uid) {
      console.log('🔍 Charts loadDataSources: No user')
      setDataSources([])
      return
    }
    
    // Use the same organization ID approach as DataSources page
    const organizationId = currentUser.uid || 'default-org'
    
    console.log('🔍 Charts loadDataSources: Starting...', {
      userId: currentUser.uid,
      organizationId: organizationId
    })
    
    setLoadingDataSources(true)
    try {
      const response = await dataSourcesService.getUserDataSources(organizationId)
      console.log('🔍 Charts loadDataSources: Response received', response)
      
      // Ensure all data sources have proper structure (same as DataSources page)
      const safeDataSources = (response.dataSources || []).map(source => ({
        ...source,
        syncSettings: source.syncSettings || { enabled: false, frequency: 'manual', autoRefreshCharts: false },
        configuration: source.configuration || {},
        tags: source.tags || [],
        usageStats: source.usageStats || { chartsUsingSource: 0, totalQueries: 0, dataVolumeProcessed: 0 },
        permissions: source.permissions || { read: [], write: [], admin: [] }
      }));
      
      setDataSources(safeDataSources)
      console.log('🔍 Charts loadDataSources: Data sources set', safeDataSources.length)
      
      // DEBUG: Log each data source with its details
      console.log('📊 DEBUG - All Data Sources:')
      safeDataSources.forEach((ds, index) => {
        console.log(`  ${index + 1}. ${ds.name}:`, {
          id: ds.id,
          type: ds.type,
          status: ds.status,
          isUsable: ds.status === 'connected' || ds.status === 'active'
        })
      })
      
      const connectedSources = safeDataSources.filter(ds => ds.status === 'connected' || ds.status === 'active')
      console.log('✅ Connected/Active Data Sources:', connectedSources.length)
      console.log('🎯 Should show suggestions:', connectedSources.length > 0)
      
    } catch (error) {
      console.error('❌ Charts loadDataSources: Error loading data sources:', error)
      setDataSources([])
    } finally {
      setLoadingDataSources(false)
    }
  }

  // Load charts from database - wait for both user and organization to be available
  useEffect(() => {
    console.log('[Charts] useEffect triggered - currentUser:', currentUser?.uid, 'currentOrganization:', currentOrganization?.id);
    if (currentUser?.uid && currentOrganization?.id) {
      console.log('[Charts] Both user and organization available, loading charts...');
      loadDbCharts()
      loadDataSources()
    } else {
      console.log('[Charts] Waiting for user and organization to be available...');
      // Clear charts while waiting for proper authentication state
      setDatabaseCharts([]);
      setLoadingDatabaseCharts(false);
    }
  }, [currentUser, currentOrganization])

  // Debug loaded database charts
  useEffect(() => {
    console.log('[ChartDebug] Database charts loaded:', databaseCharts.length);
    databaseCharts.forEach((chart, index) => {
      console.log(`[ChartDebug] Chart ${index + 1}:`, {
        id: chart.id,
        title: chart.title,
        type: chart.type,
        datasets: chart.datasets?.length,
        hasData: chart.datasets?.some(d => d.data?.length > 0)
      });
    });
  }, [databaseCharts])

  // Function to refresh charts after creation
  const refreshCharts = () => {
    loadDbCharts();
  };

  // ADDED: Effect to update chart in open studies dialog if it changes in the main list
  useEffect(() => {
    if (studiesDialog.isOpen && studiesDialog.isDatabase && studiesDialog.chart) {
      const updatedChartInList = databaseCharts.find(c => c.id === studiesDialog.chart!.id);
      // Check if the chart object in the dialog is different from the one in the main list
      // This simple reference check might not be enough if mutations happen, but ChartService.update usually returns new objects.
      // A deep equality check or version/timestamp check would be more robust if needed.
      if (updatedChartInList && JSON.stringify(updatedChartInList) !== JSON.stringify(studiesDialog.chart)) {
        console.log('[Charts.tsx] Detected change in DB chart while studies dialog is open. Updating dialog chart.');
        setStudiesDialog(prev => ({ ...prev, chart: updatedChartInList }));
      }
    }
  }, [databaseCharts, studiesDialog.isOpen, studiesDialog.isDatabase, studiesDialog.chart]);

  const handleSaveToDatabase = async (chart: ChartData) => {
    console.log('[ChartSaveDebug] Save function called for chart:', chart.title);
    console.log('[ChartSaveDebug] Current user:', currentUser?.uid);
    console.log('[ChartSaveDebug] Current organization:', currentOrganization?.id);
    console.log('[ChartSaveDebug] Already saving?', savingCharts.has(chart.id));
    
    if (!currentUser?.uid || !currentOrganization?.id || savingCharts.has(chart.id)) {
      console.log('[ChartSaveDebug] Save aborted due to missing user/org or already saving');
      return;
    }

    console.log('[ChartSaveDebug] Starting save for local chart:', JSON.parse(JSON.stringify(chart)));
    setSavingCharts(prev => new Set(prev).add(chart.id));

    try {
      const localConfig = chart.configuration || {};
      const finalConfiguration: Partial<ChartConfiguration> = {
        responsive: true,
        maintainAspectRatio: false,
        ...localConfig,
      };

      if (finalConfiguration.colorPalette === undefined) delete finalConfiguration.colorPalette;
      if (finalConfiguration.singleColor === undefined) delete finalConfiguration.singleColor;

      if (!finalConfiguration.colorPalette && !finalConfiguration.singleColor) {
        finalConfiguration.colorPalette = 'system-0';
      }
      
      console.log('[ChartSaveDebug] Final configuration for DB:', JSON.parse(JSON.stringify(finalConfiguration)));

      // Use the proper conversion function to handle multi-series data correctly
      const universalChartForConversion: UniversalChart = {
        title: chart.title || 'Untitled Chart',
        data: chart.data,
        chartType: chart.chartType,
        xAxis: chart.xAxis,
        yAxis: chart.yAxis,
        configuration: finalConfiguration,
        studies: chart.studies,
        axisOptions: chart.axisOptions,
        displayOptions: chart.displayOptions || { showGridLines: true },
        metadata: chart.metadata,
        organizationId: currentOrganization.id,
        userId: currentUser.uid,
        tags: [],
        isPublic: false
      };

      // Pre-determine colors to ensure they're stored in the database
      const normalizedForColors = normalizeChartData(universalChartForConversion, {
        customPalettes: [],
        isDarkMode: false
      });
      
      // Add the determined colors to the chart before conversion
      universalChartForConversion.colors = normalizedForColors.colors;

      const chartDataForDb = convertUniversalToDatabase(universalChartForConversion);

      // Remove undefined fields and the id field (Firestore auto-generates it)
      // Also recursively clean nested objects
      const cleanObject = (obj: any): any => {
        if (obj === null || obj === undefined) return undefined;
        if (Array.isArray(obj)) {
          return obj.map(cleanObject).filter(item => item !== undefined);
        }
        if (typeof obj === 'object') {
          const cleaned: any = {};
          for (const [key, value] of Object.entries(obj)) {
            if (key !== 'id' && value !== undefined) {
              const cleanedValue = cleanObject(value);
              if (cleanedValue !== undefined) {
                cleaned[key] = cleanedValue;
              }
            }
          }
          return Object.keys(cleaned).length > 0 ? cleaned : undefined;
        }
        return obj;
      };

      const cleanedChartData = cleanObject(chartDataForDb);

      console.log('[ChartSaveDebug] Original chartDataForDb:', JSON.parse(JSON.stringify(chartDataForDb)));
      console.log('[ChartSaveDebug] Cleaned data being sent to ChartService.create:', JSON.parse(JSON.stringify(cleanedChartData)));
      
      const savedChart = await ChartService.create(cleanedChartData as CreateChart);
      console.log('[ChartSaveDebug] Chart saved successfully:', savedChart.id);
      
      updateLocalChart(chart.id, { 
        savedToDatabase: true, 
        organizationId: currentOrganization.id,
      });

      setDatabaseCharts(prev => [savedChart, ...prev].sort((a,b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()));
      removeLocalChart(chart.id);

    } catch (error) {
      console.error('[ChartSaveDebug] Error saving chart to database:', error);
    } finally {
      setSavingCharts(prev => {
        const newSet = new Set(prev);
        newSet.delete(chart.id);
        return newSet;
      });
    }
  };

  const openStudiesDialog = (chart: ChartData | DBChart, isDatabase: boolean) => {
    console.log('[ChartsPage] openStudiesDialog - chart.configuration BEFORE setting state:', JSON.parse(JSON.stringify(chart?.configuration)));
    setStudiesDialog({
      isOpen: true,
      chart,
      isDatabase,
    })
  }

  const handleApplyStudiesToChart = async (chartToUpdate: ChartData | DBChart | null, isDbChart: boolean, newStudies: ChartStudy[], newChartType: ChartType, newAxisOptions: AxisOptions | null, newDisplayOptions: { showGridLines: boolean } | null) => {
    if (!chartToUpdate) {
      setStudiesDialog({ isOpen: false, chart: null, isDatabase: false }); // Close if no chart to update
      return;
    }
    
    try {
      const cleanStudies = newStudies.map(study => {
        const { id, type, name, color, visible, period, degree, alpha } = study;
        const cleanStudy: ChartStudy = { id, type, name, color, visible };
        if (period !== undefined) cleanStudy.period = period;
        if (degree !== undefined) cleanStudy.degree = degree;
        if (alpha !== undefined) cleanStudy.alpha = alpha;
        return cleanStudy;
      });

      // Create payload without undefined values (Firebase doesn't support undefined)
      const updatesPayload: Partial<UpdateChart | ChartData> = {
          studies: cleanStudies,
      };

      if (newAxisOptions !== null) {
          updatesPayload.axisOptions = newAxisOptions;
      }
      // Add displayOptions to the payload
      if (newDisplayOptions !== null) {
        updatesPayload.displayOptions = newDisplayOptions;
      }

      if (isDbChart) {
          (updatesPayload as Partial<UpdateChart>).type = newChartType;
      } else {
          (updatesPayload as Partial<ChartData>).chartType = newChartType as ChartData['chartType'];
      }

      if (isDbChart && currentUser && currentOrganization && chartToUpdate.id) {
        await ChartService.update(currentOrganization.id, currentUser.uid, chartToUpdate.id, updatesPayload as UpdateChart); 
        setDatabaseCharts(prev => 
          prev.map(c => 
            c.id === chartToUpdate!.id ? { ...c, ...updatesPayload, type: newChartType, updatedAt: new Date() } : c
          )
        );
      } else if (!isDbChart) {
        updateLocalChart((chartToUpdate as ChartData).id, updatesPayload as Partial<ChartData>);
      }
    } catch (error) {
      console.error("Error applying studies/type/axis options:", error);
      // Optionally, show a toast message to the user about the error
    } finally {
      setStudiesDialog({ isOpen: false, chart: null, isDatabase: false });
    }
  };

  const renderStudiesPreviewChart = (chartToPreview: ChartData | DBChart | any, studiesToApply: ChartStudy[]): JSX.Element | null => {
    console.log('[ChartsPage] renderStudiesPreviewChart - chartToPreview.configuration:', JSON.parse(JSON.stringify(chartToPreview?.configuration)));
    
    // Convert to universal format if it's a database chart
    let universalChart: ChartData;
    if (chartToPreview.datasets) {
      // It's a database chart, convert it
      universalChart = convertDatabaseChartToUniversal(chartToPreview);
    } else {
      // It's already a local/universal chart
      universalChart = chartToPreview;
    }
    
    const config = normalizeChartData(universalChart, { 
      height: 400,
      customPalettes,
      isDarkMode,
      globalDefaultPaletteId: activeDefaultPaletteId,
      enableAdvancedFeatures: true,
    });
    
    config.studies = studiesToApply;
    
    return <UniversalChartRenderer config={config} />;
  };

  const renderChart = (chart: ChartData): JSX.Element | null => {
    const config = normalizeChartData(chart, { 
      height: 350, 
      enableAdvancedFeatures: true,
      customPalettes,
      isDarkMode,
      globalDefaultPaletteId: activeDefaultPaletteId
    });

    return <UniversalChartRenderer config={config} />;
  };

  const handleDeleteDatabaseChart = async (chartId: string) => {
    if (!currentUser?.uid || !currentOrganization?.id) return
    
    try {
      await ChartService.delete(currentOrganization.id, currentUser.uid, chartId)
      // Remove the chart from local state instead of refreshing the entire list
      setDatabaseCharts(prev => prev.filter(chart => chart.id !== chartId))
    } catch (error) {
      console.error('Error deleting database chart:', error)
    }
  }

  const confirmDeleteChart = (chartId: string, chartTitle: string, isDatabase: boolean) => {
    setDeleteConfirmation({
      isOpen: true,
      chartId,
      chartTitle,
      isDatabase
    })
  }

  const handleConfirmDelete = async () => {
    const { chartId, isDatabase } = deleteConfirmation
    
    if (isDatabase) {
      await handleDeleteDatabaseChart(chartId)
    } else {
      removeLocalChart(chartId)
    }
    
    setDeleteConfirmation({
      isOpen: false,
      chartId: '',
      chartTitle: '',
      isDatabase: false
    })
  }

  const handleUpdateDatabaseChartTitle = async (chartId: string, newTitle: string) => {
    if (!currentUser?.uid || !currentOrganization?.id) return
    
    try {
      await ChartService.updateTitle(currentOrganization.id, currentUser.uid, chartId, newTitle)
      // Update the local state
      setDatabaseCharts(prev => 
        prev.map(chart => 
          chart.id === chartId ? { ...chart, title: newTitle } : chart
        )
      )
    } catch (error) {
      console.error('Error updating database chart title:', error)
      throw error // Re-throw to keep edit mode open
    }
  }

  // Callback to refresh charts after data update
  const handleChartDataUpdateOnChartsPage = (isDbChart: boolean) => {
    if (isDbChart) {
      console.log('ChartsPage: DB chart data updated, re-fetching DB charts.');
      loadDbCharts(); // Re-fetch database charts
    } else {
      // For local charts, direct modification of Jotai atom might be complex here.
      // The ChartDataEditorDialog currently works with DBChart structure for saving.
      // If local chart data editing is needed, ChartDataEditorDialog would need to handle ChartData type
      // and we'd need a way to update the specific Jotai atom for that local chart.
      console.warn('ChartsPage: Local chart data update requested, but not fully implemented for direct refresh here. Dialog assumes DBChart for now.');
    }
  };

  const openDataEditorDialog = (chart: DBChart, isDatabase: boolean) => {
    if (isDatabase) {
      setDataEditorDialog({
        isOpen: true,
        chart: chart as DBChart, 
        isDatabase: true,
      });
    } else {
      console.warn("Data editing for local, unsaved charts via this dialog is not yet supported.");
    }
  };

  const handleEditLocalChart = (chart: ChartData) => {
    setLocalEditDialog({
      isOpen: true,
      chartTitle: chart.title || 'Untitled Chart',
    });
  };

  const handleLocalChartSingleColorChange = async (chartId: string, color: ChartColor) => {
    const chart = localChartsData.find(c => c.id === chartId);
    if (!chart) return;
    updateLocalChart(chartId, {
      configuration: {
        ...(chart.configuration || {}),
        singleColor: color,
        colorPalette: undefined, // Ensure colorPalette is cleared
      }
    } as Partial<ChartData>);
  };

  const handleDatabaseChartSingleColorChange = async (chartId: string, color: ChartColor) => {
    if (!currentUser?.uid || !currentOrganization?.id) return;
    
    try {
      const chart = databaseCharts.find(c => c.id === chartId);
      if (!chart) return;

      const updatedConfiguration: Partial<ChartConfiguration> = {
        ...(chart.configuration || {}),
        singleColor: color,
      };
      delete updatedConfiguration.colorPalette; // Ensure colorPalette is cleared
      if (updatedConfiguration.singleColor === undefined) delete updatedConfiguration.singleColor; // Clean if color is undefined

      await ChartService.update(currentOrganization.id, currentUser.uid, chartId, {
        configuration: updatedConfiguration as ChartConfiguration,
      });

      setDatabaseCharts(prev =>
        prev.map(c =>
          c.id === chartId
            ? { ...c, configuration: updatedConfiguration as ChartConfiguration, updatedAt: new Date() }
            : c
        )
      );
    } catch (error) {
      console.error('Error updating DB chart single color:', error);
    }
  };

  const handleChartCreated = () => {
    loadDbCharts(); // Refresh charts after creation
  };

  return (
    <Sidebar>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border pb-5">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold leading-tight text-foreground">Charts</h1>
              <p className="mt-2 max-w-4xl text-sm text-muted-foreground mr-4">
                Create stunning visualizations with AI assistance using your connected data sources, or upload CSV files for traditional chart creation.
              </p>
            </div>
          </div>
          
          {/* Create Chart Button - Show when charts exist */}
          {(localChartsData.length > 0 || databaseCharts.length > 0) && (
            <div className="flex gap-2">
              <Button onClick={() => setIsUnifiedCreatorOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Chart
              </Button>
              <Button variant="outline" onClick={() => setShowQuickCreate(true)}>
                <CloudUpload className="h-4 w-4 mr-2" />
                Upload CSV
              </Button>
            </div>
          )}
        </div>

        {/* Chart Creator */}
        {localChartsData.length === 0 && databaseCharts.length === 0 && !showQuickCreate && !loadingDatabaseCharts ? (
          /* Create Your First Chart */
          <Card className="text-center py-12 border-2 border-dashed border-primary/20 bg-gradient-to-br from-primary/5 to-orange/5">
            <CardContent>
              <div className="relative mb-6">
                <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                <Sparkles className="h-6 w-6 text-orange-500 absolute -top-1 -right-8" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">Create Your First AI-Powered Chart</h3>
              <p className="text-muted-foreground mb-6">
                Simply describe what you want to visualize, and our AI will create beautiful charts from your connected data sources.
              </p>
              <Button onClick={() => setIsUnifiedCreatorOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white">
                <Sparkles className="h-4 w-4 mr-2" />
                Create Chart with AI
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {/* Legacy CSV Chart Creator */}
        {showQuickCreate && (
          <QuickChartCreator 
            isVisible={true}
            onClose={() => setShowQuickCreate(false)}
            onSuccess={() => setShowQuickCreate(false)}
          />
        )}

        {/* Local Charts section removed - using unified auto-save model */}

        {/* Charts */}
        {loadingDatabaseCharts && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">Loading charts...</span>
          </div>
        )}

        {databaseCharts.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Charts ({databaseCharts.length})</h2>
              
              {/* Sort Options */}
              <div className="flex items-center gap-2">
                <SlidingToggle
                  options={SORT_OPTIONS}
                  value={databaseChartSort}
                  onChange={(value) => setDatabaseChartSort(value as SortOption)}
                  className="w-72"
                  textSize="xs"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               {sortedDatabaseCharts.map((chart: ChartData) => (
                <Card key={chart.id} className="flex flex-col hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3 space-y-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            {(() => { const Icon = getChartIconComponent(chart.chartType); return <Icon className="h-5 w-5 text-muted-foreground" />; })()}
                            <EditableChartTitle
                                title={chart.title || 'Untitled Chart'}
                                onSave={async (newTitle) => { await handleUpdateDatabaseChartTitle(chart.id, newTitle); }}
                                className="text-lg font-semibold flex-1"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-1 pt-1 border-t border-border -mx-6 px-6">
                        <ChartTypeSelector
                            currentType={chart.chartType as ChartType}
                            onTypeChange={async (newType) => {
                                if (currentUser?.uid && currentOrganization?.id) {
                                    try {
                                        await ChartService.update(currentOrganization.id, currentUser.uid, chart.id, { type: newType as ChartType });
                                        setDatabaseCharts(prev => prev.map(c => c.id === chart.id ? { ...c, chartType: newType as ChartData['chartType'], updatedAt: new Date() } : c));
                                    } catch (error) { console.error('Error updating database chart type:', error); }
                                }
                            }}
                        />
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                            <Button variant="ghost" size="icon" onClick={() => openStudiesDialog(chart, true)} title="Chart Studies" className="h-8 w-8 p-0">
                                <Settings className="h-4 w-4" />
                            </Button>
                            <ChartColorPaletteChanger
                                className="h-8 w-8"
                                chartType={chart.chartType as ChartType}
                                currentColor={chart.configuration?.singleColor}
                                onColorChange={(color) => handleDatabaseChartSingleColorChange(chart.id, color)}
                            />
                            <Button variant="ghost" size="icon" onClick={() => confirmDeleteChart(chart.id, chart.title || 'Untitled Chart', true)} title="Delete Chart" className="h-8 w-8 p-0 text-destructive hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-grow flex flex-col justify-center">
                    {renderChart(chart)}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Studies Dialog - Now a separate component */}
        {studiesDialog.isOpen && studiesDialog.chart && (
          <ChartStudiesDialog
            isOpen={studiesDialog.isOpen}
            onClose={() => setStudiesDialog({ isOpen: false, chart: null, isDatabase: false })}
            chart={studiesDialog.chart}
            isDatabaseChart={studiesDialog.isDatabase}
            initialStudies={studiesDialog.chart.studies || []} 
            onApplyStudies={async (updatedStudiesFromDialog, updatedTypeFromDialog, updatedAxisOptionsFromDialog, updatedDisplayOptionsFromDialog) => { 
              const currentChart = studiesDialog.isDatabase 
                ? databaseCharts.find(c => c.id === studiesDialog.chart?.id)
                : localChartsData.find(c => c.id === studiesDialog.chart?.id);
                
              if (currentChart) {
                await handleApplyStudiesToChart(currentChart, studiesDialog.isDatabase, updatedStudiesFromDialog, updatedTypeFromDialog, updatedAxisOptionsFromDialog, updatedDisplayOptionsFromDialog);
              }
            }}
            currentUser={currentUser} 
            currentOrganization={currentOrganization}
            renderPreviewFunction={renderStudiesPreviewChart}
            onChartUpdate={() => loadDbCharts()}
            onChartTitleUpdate={studiesDialog.isDatabase ? handleUpdateDatabaseChartTitle : undefined}
            onChartPaletteChange={studiesDialog.isDatabase ? handleDatabaseChartSingleColorChange : handleLocalChartSingleColorChange}
            onChartColorChange={studiesDialog.isDatabase ? handleDatabaseChartSingleColorChange : handleLocalChartSingleColorChange}
          />
        )}

        {/* Data Editor Dialog */}
        {dataEditorDialog.isOpen && dataEditorDialog.chart && (
          <ChartDataEditorDialog
            isOpen={dataEditorDialog.isOpen}
            onClose={() => setDataEditorDialog({ isOpen: false, chart: null, isDatabase: false })}
            chart={dataEditorDialog.chart} // This is now correctly typed as DBChart | null
            onChartUpdate={() => handleChartDataUpdateOnChartsPage(dataEditorDialog.isDatabase)}
          />
        )}

        {/* Delete Confirmation Dialog */}
        {deleteConfirmation.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-2">Delete Chart</h3>
              <p className="text-muted-foreground mb-4">
                Are you sure you want to delete "{deleteConfirmation.chartTitle}"? 
                {deleteConfirmation.isDatabase && (
                  <span className="block mt-2 text-destructive font-medium">
                    This will permanently delete the chart and all its data from the database.
                  </span>
                )}
                This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirmation({
                    isOpen: false,
                    chartId: '',
                    chartTitle: '',
                    isDatabase: false
                  })}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleConfirmDelete}
                >
                  Delete Chart
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Local Chart Edit Dialog */}
        {localEditDialog.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-2">Edit Chart Data</h3>
              <p className="text-muted-foreground mb-4">
                To edit the data for "{localEditDialog.chartTitle}", you first need to save it to the database.
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Click the <CloudUpload className="h-4 w-4 inline mx-1" /> cloud upload icon to save this chart to the database, then you'll be able to edit its data.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setLocalEditDialog({
                    isOpen: false,
                    chartTitle: '',
                  })}
                >
                  Got it
                </Button>
              </div>
            </div>
          </div>
        )}

        <UnifiedAIChartCreator
          isOpen={isUnifiedCreatorOpen}
          onClose={() => setIsUnifiedCreatorOpen(false)}
          dataSources={dataSources}
          onChartCreated={handleChartCreated}
        />
      </div>
     
    </Sidebar>
  )
}

export default Charts 